/**
 * Notification Service — centralized data access for the in-app notification system.
 *
 * Steps 1 and 2 of the notification system overhaul (see docs/notification-system-overhaul.md).
 *
 * Provides:
 *   - One-shot fetchers (Step 1) — thin wrappers around Firebase.ts methods.
 *   - Real-time subscriptions (Step 2) — onSnapshot-based listeners that fire on every change.
 *   - Action helpers — claim / dismiss / approve / reject / acknowledge.
 *
 * All notification consumers (bell, modals, future inbox, future email triggers) route through
 * this module so subsequent changes (preferences, unified `notifications` collection, etc.)
 * can be made in one place.
 *
 * Notification types covered:
 *   - Pending licenses          (client side: licenses awaiting claim)
 *   - Rejected license requests (client side: declined by the artist org)
 *   - Pending license requests  (artist side: incoming requests to approve / reject)
 *   - Expired licenses          (client side: expired or revoked, not yet acknowledged)
 *   - Exclusive license endings (artist side: exclusive deal ended)
 */

import {
  collection,
  collectionGroup,
  doc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
  Unsubscribe,
} from 'firebase/firestore';
import { ORGANIZATIONSPATH } from '../constants/paths';
import {
  NotificationPreferences,
  mergeWithDefaults,
} from './notificationPreferences';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PendingLicense {
  id: string;
  licenseId: string;
  trackId: string;
  trackName: string;
  sourceOrgId: string;
  sourceOrgName: string;
  licenseType: string;
  dateExpires?: number;
  dateStart?: number;
  sharedAt?: number;
  licensePath: string;
}

export interface RejectedLicenseRequest {
  id: string;
  trackId: string;
  trackName: string;
  artistOrgId: string;
  licenseType: string;
  rejectedAt: number;
  rejectionReason?: string;
}

export interface LicenseRequestField {
  label: string;
  value: string;
}

export interface PendingLicenseRequest {
  id: string;
  trackId: string;
  trackName: string;
  requestingOrgId: string;
  requestingOrgName: string;
  licenseType: string;
  dateExpires: number | null;
  dateStart: number | null;
  jobTitle?: string;
  client?: LicenseRequestField | null;
  agency?: LicenseRequestField | null;
  flightingPlatform?: LicenseRequestField[];
  territories?: LicenseRequestField[];
  notes?: string;
  createdAt: number;
  createdByName?: string;
  status: string;
}

export interface ExpiredLicense {
  id: string;
  licenseId: string;
  trackId: string;
  trackName?: string;
  sourceOrgId: string;
  sourceOrgName?: string;
  clientOrgId?: string;
  clientOrgName?: string;
  licenseType: string;
  dateExpires?: number;
  deactivatedAt?: number;
  licensePath: string;
  /** Which side of the license this org is on: 'client' (held it) or 'artist' (issued it). */
  recipientRole: 'client' | 'artist';
  /** Name of the other party — client name for artist-side, source org name for client-side. */
  otherPartyOrgName?: string;
}

export interface ExclusiveLicenseNotification {
  id: string;
  trackId: string;
  trackName: string;
  licenseId: string;
  clientName: string;
  endedAt: number;
  type: 'revoked' | 'expired';
  dismissed: boolean;
  republished: boolean;
}

/**
 * Notification for the artist when a pending license they issued is claimed
 * (via direct share or share-link). Stored at
 * `organizations/{artistOrgId}/licenseClaimNotifications/{licenseId}`.
 */
export interface LicenseClaimNotification {
  id: string;
  trackId: string;
  trackName: string;
  licenseId: string;
  licensePath: string;
  licenseType: string;
  clientOrgId: string;
  clientOrgName: string;
  claimedAt: number;
  claimedBy: string;
  wasShareLink: boolean;
  createdAt: number;
  dismissed: boolean;
  dismissedAt?: number;
}

/**
 * Pre-expiry alert written by the scheduled Cloud Function when a license is
 * within the org's lead-time window. Stored at
 * `organizations/{orgId}/expiringLicenseAlerts/{alertId}`.
 */
export interface ExpiringLicenseAlert {
  id: string;
  trackId: string;
  trackName: string;
  licenseId: string;
  licensePath: string;
  licenseType: string;
  recipientRole: 'artist' | 'client';
  otherPartyOrgId: string;
  otherPartyOrgName: string;
  dateExpires: number;
  leadTimeDays: number;
  createdAt: number;
  dismissed: boolean;
  dismissedAt?: number;
}

// The Firebase service class is untyped (`any`). Keep that contract here so the
// service can be called from both TS and JS consumers without friction.
// Subscriptions only need `firebase.db`; actions only need the existing methods.
type FirebaseService = any;

// ---------------------------------------------------------------------------
// Fetchers — one-shot reads. Kept for callers that don't want a live listener.
// ---------------------------------------------------------------------------

export const fetchPendingLicenses = (
  firebase: FirebaseService,
  orgId: string,
): Promise<PendingLicense[]> => firebase.getPendingLicensesForOrg(orgId);

export const fetchRejectedLicenseRequests = (
  firebase: FirebaseService,
  orgId: string,
): Promise<RejectedLicenseRequest[]> =>
  firebase.getRejectedLicenseRequestsForClient(orgId);

export const fetchPendingLicenseRequests = (
  firebase: FirebaseService,
  orgId: string,
): Promise<PendingLicenseRequest[]> =>
  firebase.getPendingLicenseRequestsForOrg(orgId);

export const fetchExpiredLicenses = async (
  firebase: FirebaseService,
  orgId: string,
): Promise<ExpiredLicense[]> => {
  const [expired, deactivated] = await Promise.all([
    firebase.getExpiredLicensesForClient(orgId),
    firebase.getDeactivatedLicensesForClient(orgId),
  ]);
  const combined: ExpiredLicense[] = [...expired, ...deactivated].map((l) => ({
    ...l,
    recipientRole: 'client' as const,
  }));
  return combined.filter(
    (license, index, self) =>
      index === self.findIndex((l) => l.id === license.id),
  );
};

export const fetchExclusiveLicenseNotifications = (
  firebase: FirebaseService,
  orgId: string,
): Promise<ExclusiveLicenseNotification[]> =>
  firebase.getExclusiveLicenseNotifications(orgId);

// ---------------------------------------------------------------------------
// Subscriptions — real-time listeners. Each returns an Unsubscribe function;
// callers should invoke it on cleanup (e.g., in useEffect's return).
// ---------------------------------------------------------------------------

/**
 * Pending licenses shared to a client org, awaiting claim.
 * Mirrors `firebase.getPendingLicensesForOrg` but live.
 */
export const subscribePendingLicenses = (
  firebase: FirebaseService,
  orgId: string,
  callback: (licenses: PendingLicense[]) => void,
): Unsubscribe => {
  const q = query(
    collectionGroup(firebase.db, 'licenses'),
    where('clientOrgId', '==', orgId),
    where('status', '==', 'pending'),
  );
  return onSnapshot(
    q,
    (snapshot) => {
      const licenses = snapshot.docs.map((licenseDoc) => {
        const data = licenseDoc.data();
        // Path: organizations/{sourceOrgId}/audiometa/{trackId}/licenses/{licenseId}
        const segments = licenseDoc.ref.path.split('/');
        return {
          id: licenseDoc.id,
          licenseId: licenseDoc.id,
          trackId: segments[3],
          trackName: data.trackName || 'Unknown Track',
          sourceOrgId: segments[1],
          sourceOrgName: data.sourceOrgName || 'Unknown Organization',
          licenseType: data.licenseType || 'unknown',
          dateExpires: data.dateExpires,
          dateStart: data.dateStart,
          sharedAt: data.sharedAt,
          licensePath: licenseDoc.ref.path,
        };
      });
      callback(licenses);
    },
    (error) => {
      console.error('subscribePendingLicenses error:', error);
      callback([]);
    },
  );
};

/**
 * Rejected license requests for a client org that haven't been dismissed.
 * Client-side filters out `dismissedByClient === true` (missing field doesn't match `where`).
 */
export const subscribeRejectedLicenseRequests = (
  firebase: FirebaseService,
  orgId: string,
  callback: (requests: RejectedLicenseRequest[]) => void,
): Unsubscribe => {
  const q = query(
    collectionGroup(firebase.db, 'licenseRequests'),
    where('requestingOrgId', '==', orgId),
    where('status', '==', 'rejected'),
    orderBy('rejectedAt', 'desc'),
  );
  return onSnapshot(
    q,
    (snapshot) => {
      const requests = snapshot.docs
        .map((d) => ({ id: d.id, ...(d.data() as any) }))
        .filter((r: any) => r.dismissedByClient !== true) as RejectedLicenseRequest[];
      callback(requests);
    },
    (error) => {
      console.error('subscribeRejectedLicenseRequests error:', error);
      callback([]);
    },
  );
};

/**
 * Incoming license requests for an artist org to approve or reject.
 */
export const subscribePendingLicenseRequests = (
  firebase: FirebaseService,
  orgId: string,
  callback: (requests: PendingLicenseRequest[]) => void,
): Unsubscribe => {
  const requestsRef = collection(firebase.db, ORGANIZATIONSPATH, orgId, 'licenseRequests');
  const q = query(
    requestsRef,
    where('status', '==', 'pending'),
    orderBy('createdAt', 'desc'),
  );
  return onSnapshot(
    q,
    (snapshot) => {
      const requests = snapshot.docs.map((d) => ({
        id: d.id,
        ...(d.data() as any),
      })) as PendingLicenseRequest[];
      callback(requests);
    },
    (error) => {
      console.error('subscribePendingLicenseRequests error:', error);
      callback([]);
    },
  );
};

/**
 * Expired and revoked licenses for an org — surfaces BOTH sides of the
 * license (as client / holder AND as artist / issuer) in a single feed.
 *
 * Runs four underlying listeners:
 *   - clientOrgId=orgId × status=active   → licenses this org holds that expired by date
 *   - clientOrgId=orgId × status=inactive → licenses this org held that got revoked
 *   - sourceOrgId=orgId × status=active   → licenses this org issued that expired by date
 *   - sourceOrgId=orgId × status=inactive → licenses this org issued that got revoked
 *
 * Each result is tagged with `recipientRole` so callers can adapt copy.
 * Deduplication is by (id + role) — a license where this org is on both
 * sides (unusual, but possible for self-shares) shows once per role.
 *
 * Acknowledgement uses two flags on the license doc:
 *   - clientAcknowledgedExpiry (existing) — client-side dismiss
 *   - artistAcknowledgedExpiry (new)      — artist-side dismiss
 */
export const subscribeExpiredLicenses = (
  firebase: FirebaseService,
  orgId: string,
  callback: (licenses: ExpiredLicense[]) => void,
): Unsubscribe => {
  const buckets: { key: string; items: ExpiredLicense[] }[] = [
    { key: 'client-expired',   items: [] },
    { key: 'client-revoked',   items: [] },
    { key: 'artist-expired',   items: [] },
    { key: 'artist-revoked',   items: [] },
  ];

  const emit = () => {
    const combined = buckets.flatMap((b) => b.items);
    // Dedupe by id+role — same license doc could theoretically appear if the
    // same org is on both sides.
    const seen = new Set<string>();
    const unique = combined.filter((l) => {
      const key = `${l.id}:${l.recipientRole}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    callback(unique);
  };

  // Path: organizations/{sourceOrgId}/audiometa/{trackId}/licenses/{licenseId}
  const projectLicense = (docSnap: any, role: 'client' | 'artist'): ExpiredLicense => {
    const data = docSnap.data();
    const segments = docSnap.ref.path.split('/');
    const otherPartyOrgName = role === 'artist'
      ? (data.clientOrgName || '')
      : (data.sourceOrgName || '');
    return {
      id: docSnap.id,
      licenseId: docSnap.id,
      trackId: segments[3],
      sourceOrgId: segments[1],
      licensePath: docSnap.ref.path,
      recipientRole: role,
      otherPartyOrgName,
      ...data,
    };
  };

  const runListener = (
    filterField: 'clientOrgId' | 'sourceOrgId',
    statusValue: 'active' | 'inactive',
    role: 'client' | 'artist',
    bucketKey: string,
    filterFn: (l: any) => boolean,
    label: string,
  ) => {
    const q = query(
      collectionGroup(firebase.db, 'licenses'),
      where(filterField, '==', orgId),
      where('status', '==', statusValue),
    );
    return onSnapshot(
      q,
      (snapshot) => {
        const bucket = buckets.find((b) => b.key === bucketKey);
        if (!bucket) return;
        bucket.items = snapshot.docs
          .map((d) => projectLicense(d, role))
          .filter(filterFn);
        emit();
      },
      (error) => {
        console.error(`subscribeExpiredLicenses (${label}) error:`, error);
        const bucket = buckets.find((b) => b.key === bucketKey);
        if (bucket) bucket.items = [];
        emit();
      },
    );
  };

  const clientExpiredFilter = (l: any) => {
    const now = Date.now();
    return !!(l.dateExpires && l.dateExpires < now) && l.clientAcknowledgedExpiry !== true;
  };
  const clientRevokedFilter = (l: any) =>
    l.deactivatedAt != null && l.clientAcknowledgedExpiry !== true;
  const artistExpiredFilter = (l: any) => {
    const now = Date.now();
    return !!(l.dateExpires && l.dateExpires < now) && l.artistAcknowledgedExpiry !== true;
  };
  const artistRevokedFilter = (l: any) =>
    l.deactivatedAt != null && l.artistAcknowledgedExpiry !== true;

  const unsubClientExpired   = runListener('clientOrgId', 'active',   'client', 'client-expired', clientExpiredFilter, 'client-expired');
  const unsubClientRevoked   = runListener('clientOrgId', 'inactive', 'client', 'client-revoked', clientRevokedFilter, 'client-revoked');
  const unsubArtistExpired   = runListener('sourceOrgId', 'active',   'artist', 'artist-expired', artistExpiredFilter, 'artist-expired');
  const unsubArtistRevoked   = runListener('sourceOrgId', 'inactive', 'artist', 'artist-revoked', artistRevokedFilter, 'artist-revoked');

  return () => {
    unsubClientExpired();
    unsubClientRevoked();
    unsubArtistExpired();
    unsubArtistRevoked();
  };
};

/**
 * Active pre-expiry alerts for this org (recipientRole captures whether this
 * org is the artist or the client side of the license).
 */
export const subscribeExpiringLicenseAlerts = (
  firebase: FirebaseService,
  orgId: string,
  callback: (alerts: ExpiringLicenseAlert[]) => void,
): Unsubscribe => {
  const ref = collection(firebase.db, ORGANIZATIONSPATH, orgId, 'expiringLicenseAlerts');
  const q = query(ref, where('dismissed', '==', false), orderBy('dateExpires', 'asc'));
  return onSnapshot(
    q,
    (snapshot) => {
      const alerts = snapshot.docs.map((d) => ({
        id: d.id,
        ...(d.data() as any),
      })) as ExpiringLicenseAlert[];
      callback(alerts);
    },
    (error) => {
      console.error('subscribeExpiringLicenseAlerts error:', error);
      callback([]);
    },
  );
};

/**
 * Active "license was claimed" notifications for the artist org that issued
 * the license. Fires whether the license was directly shared or claimed
 * via a share link.
 */
export const subscribeLicenseClaimNotifications = (
  firebase: FirebaseService,
  orgId: string,
  callback: (notifications: LicenseClaimNotification[]) => void,
): Unsubscribe => {
  const ref = collection(firebase.db, ORGANIZATIONSPATH, orgId, 'licenseClaimNotifications');
  const q = query(ref, where('dismissed', '==', false), orderBy('claimedAt', 'desc'));
  return onSnapshot(
    q,
    (snapshot) => {
      const notifications = snapshot.docs.map((d) => ({
        id: d.id,
        ...(d.data() as any),
      })) as LicenseClaimNotification[];
      callback(notifications);
    },
    (error) => {
      console.error('subscribeLicenseClaimNotifications error:', error);
      callback([]);
    },
  );
};

/**
 * Active exclusive-license-ending notifications for an artist org.
 */
export const subscribeExclusiveLicenseNotifications = (
  firebase: FirebaseService,
  orgId: string,
  callback: (notifications: ExclusiveLicenseNotification[]) => void,
): Unsubscribe => {
  const ref = collection(firebase.db, ORGANIZATIONSPATH, orgId, 'exclusiveLicenseNotifications');
  const q = query(ref, where('dismissed', '==', false), orderBy('createdAt', 'desc'));
  return onSnapshot(
    q,
    (snapshot) => {
      const notifications = snapshot.docs.map((d) => ({
        id: d.id,
        ...(d.data() as any),
      })) as ExclusiveLicenseNotification[];
      callback(notifications);
    },
    (error) => {
      console.error('subscribeExclusiveLicenseNotifications error:', error);
      callback([]);
    },
  );
};

// ---------------------------------------------------------------------------
// Actions — claim / dismiss / approve / reject. After a successful mutation,
// the corresponding subscription will fire and update the UI; callers no
// longer need to manually patch local state.
// ---------------------------------------------------------------------------

export const claimPendingLicense = (
  firebase: FirebaseService,
  params: {
    sourceOrgId: string;
    trackId: string;
    licenseId: string;
    claimingOrgId: string;
    claimingUserId: string;
    claimingOrgName?: string;
  },
): Promise<boolean> =>
  firebase.claimPendingLicense(
    params.sourceOrgId,
    params.trackId,
    params.licenseId,
    params.claimingOrgId,
    params.claimingUserId,
    params.claimingOrgName,
  );

export const dismissRejectedLicenseRequest = (
  firebase: FirebaseService,
  params: { artistOrgId: string; requestId: string },
): Promise<boolean> =>
  firebase.dismissRejectedLicenseRequest(params.artistOrgId, params.requestId);

export const approveLicenseRequest = (
  firebase: FirebaseService,
  params: { orgId: string; requestId: string },
): Promise<boolean> =>
  firebase.approveLicenseRequest(params.orgId, params.requestId);

export const rejectLicenseRequest = (
  firebase: FirebaseService,
  params: { orgId: string; requestId: string },
): Promise<boolean> =>
  firebase.rejectLicenseRequest(params.orgId, params.requestId);

export const acknowledgeExpiredLicense = (
  firebase: FirebaseService,
  params: {
    sourceOrgId: string;
    trackId: string;
    licenseId: string;
    /** Which side of the license this org is on — determines which ack flag to set. */
    recipientRole: 'client' | 'artist';
  },
): Promise<boolean> =>
  firebase.acknowledgeExpiredLicense(
    params.sourceOrgId,
    params.trackId,
    params.licenseId,
    params.recipientRole,
  );

export const dismissExclusiveLicenseNotification = (
  firebase: FirebaseService,
  params: { orgId: string; notificationId: string; republished: boolean },
): Promise<void> =>
  firebase.dismissExclusiveLicenseNotification(
    params.orgId,
    params.notificationId,
    params.republished,
  );

/**
 * Dismiss a license-claim notification. Marks the doc at
 * `organizations/{orgId}/licenseClaimNotifications/{notificationId}`
 * as dismissed so the subscription drops it.
 */
export const dismissLicenseClaimNotification = (
  firebase: FirebaseService,
  params: { orgId: string; notificationId: string },
): Promise<void> =>
  updateDoc(
    doc(
      firebase.db,
      ORGANIZATIONSPATH,
      params.orgId,
      'licenseClaimNotifications',
      params.notificationId,
    ),
    { dismissed: true, dismissedAt: Date.now() },
  );

/**
 * Dismiss a pre-expiry alert. The doc lives at
 * `organizations/{orgId}/expiringLicenseAlerts/{alertId}` and is marked
 * `dismissed=true` so the subscription drops it from the list.
 */
export const dismissExpiringLicenseAlert = (
  firebase: FirebaseService,
  params: { orgId: string; alertId: string },
): Promise<void> =>
  updateDoc(
    doc(
      firebase.db,
      ORGANIZATIONSPATH,
      params.orgId,
      'expiringLicenseAlerts',
      params.alertId,
    ),
    { dismissed: true, dismissedAt: Date.now() },
  );

// ---------------------------------------------------------------------------
// Preferences
// Per-type, per-channel (inApp / email) schema. See notificationPreferences.ts
// for type definitions, defaults, labels, and legacy migration.
// ---------------------------------------------------------------------------

/**
 * Load and normalize the current user's notification preferences for an org.
 * Always returns a fully-populated NotificationPreferences object (defaults
 * applied for missing fields; legacy `licenseExpiry` migrated forward).
 */
export const getNotificationPreferences = async (
  firebase: FirebaseService,
  orgId: string,
  userId: string,
): Promise<NotificationPreferences> => {
  const raw = await firebase.getMemberNotificationPreferences(orgId, userId);
  return mergeWithDefaults(raw);
};

/**
 * Save the user's notification preferences for an org. The stored shape is
 * the full new schema; callers are expected to pass a fully-populated object
 * (e.g., the one returned by getNotificationPreferences).
 */
export const saveNotificationPreferences = (
  firebase: FirebaseService,
  orgId: string,
  userId: string,
  prefs: NotificationPreferences,
): Promise<void> =>
  firebase.updateMemberNotificationPreferences(orgId, userId, prefs);
